import {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../contexts/brand-context'
import {deferScriptLoading} from './utils'
import {getSiteEnvVarValue, getBrandEnvVarValue} from './site-config'

let SDK = null
let loading = false
let identifiedUserId = null
// True after uninstall(); re-grant requires a reload because the SDK cannot re-init.
let wasUninstalled = false

// Request/response headers whose values must never be captured.
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-auth-token']

// Query-string keys whose values must be redacted from captured URLs.
const SENSITIVE_QUERY_PARAMS = [
    'token',
    'access_token',
    'refresh_token',
    'id_token',
    'code',
    'secret',
    'client_secret',
    'api_key',
    'apikey',
    'key',
    'password',
    'pwd',
    'email',
    'usid',
    'ordernumber',
    'orderemail',
    'billingpostcode',
    'c_ordernumber',
    'q'
]

// Blank out the values of sensitive headers while keeping the header names.
const scrubHeaders = (headers) => {
    if (!headers) return headers

    Object.keys(headers).forEach((name) => {
        if (SENSITIVE_HEADERS.includes(name.toLowerCase())) {
            headers[name] = ''
        }
    })

    return headers
}

// Redact sensitive query-string values from a captured URL.
const sanitizeUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl

    try {
        const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(rawUrl)
        const url = new URL(rawUrl, window.location.origin)

        url.searchParams.forEach((_, key) => {
            if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
                url.searchParams.set(key, '[REDACTED]')
            }
        })

        // Redact order identifiers embedded in storefront routes.
        url.pathname = url.pathname
            .replace(/(\/checkout\/confirmation\/)[^/?#]+/i, '$1[REDACTED]')
            .replace(/(\/account\/orders\/)[^/?#]+/i, '$1[REDACTED]')

        return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
    } catch {
        return rawUrl
    }
}

/**
 * Initialize the LogRocket browser SDK (client-side only).
 *
 * Loads the SDK and the React plugin lazily so it never ends up in the SSR
 * bundle, then starts a recording session for the configured app ID.
 *
 * @param {string} appId - Site-scoped LOGROCKET_APP_ID resolved by the caller.
 * @param {string} [environmentType] - Deploy environment, used to tag sessions.
 * @returns {Promise<object|null>} LogRocket SDK instance or null if it is disabled/failed.
 */
async function initLogRocket(appId, environmentType) {
    if (SDK) return SDK

    // LogRocket only runs in the browser.
    if (typeof window === 'undefined') return null

    // Prevent duplicate SDK initialization
    if (loading) return null

    if (!appId) {
        return null
    }

    loading = true

    try {
        const {default: LogRocket} = await import('logrocket')
        const {default: setupLogRocketReact} = await import('logrocket-react')

        LogRocket.init(appId, {
            // Tag sessions with the deploy environment when available.
            release: environmentType || undefined,
            dom: {
                // Obfuscate every user-input field from replays. PII *text* is
                // redacted per-element via `data-private` (not globally)
                inputSanitizer: true
            },
            network: {
                // Strip request bodies and sensitive headers (timing/errors are kept).
                requestSanitizer: (request) => ({
                    ...request,
                    body: undefined,
                    headers: scrubHeaders(request.headers)
                }),
                // Strip all response bodies (timing/errors are kept).
                responseSanitizer: (response) => ({
                    ...response,
                    body: undefined,
                    headers: scrubHeaders(response.headers)
                })
            },
            browser: {
                // Redact sensitive values from captured browser URLs.
                urlSanitizer: sanitizeUrl
            }
        })

        // Annotate session replays with React component names.
        setupLogRocketReact(LogRocket)

        SDK = LogRocket

        return SDK
    } catch (err) {
        console.error('LogRocket failed to initialize', err)
        return null
    } finally {
        loading = false
    }
}

// Associate the current session with a user (stable, non-PII identifier).
const identifyLogRocket = (userId, traits = {}) => {
    if (!SDK || !userId || identifiedUserId === userId) return

    SDK.identify(userId, traits)
    identifiedUserId = userId
}

// Begin a fresh recording and clear the identified user so the next identify()
// re-associates the new session. Used when the shopper identity changes
// (logout / account switch): identify() is append-only, so layering a second
// identity onto the same recording would cross-link the two shoppers.
const startNewLogRocketSession = () => {
    try {
        SDK?.startNewSession?.()
    } catch (err) {
        console.error('LogRocket failed to start a new session', err)
    }
    identifiedUserId = null
}

// Tear down an active LogRocket session when Didomi consent is withdrawn.
const disableLogRocket = () => {
    try {
        if (SDK) SDK.uninstall()
    } catch (err) {
        console.error('LogRocket failed to uninstall', err)
    } finally {
        SDK = null
        identifiedUserId = null
        wasUninstalled = true
        if (typeof window !== 'undefined' && window.LogRocket) {
            delete window.LogRocket
        }
    }
}

// Whether Didomi's current state permits LogRocket recording.
//
//   * An explicit grant for the vendor (GDPR/TCF) allows recording.
//   * Without an explicit choice, US opt-out regulations (CPA/CCPA) do not
//     require prior consent: the vendor is enabled by default and only disabled
//     once the shopper opts out. Didomi exposes that as the vendor's current
//     `enabled` flag, so recording can start without a choice being made.
//   * Consent-based regulations (GDPR) require an explicit choice, so recording
//     stays off until the shopper opts in.
//
// Without the opt-out branch LogRocket never started for CPA shoppers until they
// opened the cookie preferences and accepted, even though no consent was needed.
const hasLogRocketConsent = (vendorId) => {
    const didomi = window.Didomi
    if (!didomi) return false

    if (didomi.getUserConsentStatusForVendor?.(vendorId)) return true

    const vendorEnabled = didomi.getCurrentUserStatus?.()?.vendors?.[vendorId]?.enabled
    if (typeof vendorEnabled === 'boolean') return vendorEnabled

    // Vendor not part of the notice: fall back to whether consent is required.
    if (typeof didomi.isConsentRequired === 'function') return !didomi.isConsentRequired()

    return false
}

/**
 * Mounts and initializes LogRocket session recording on the client.
 *
 * Initialization is deferred until the browser is idle so it never blocks
 * first paint, and the current shopper is associated with the session once
 * the SDK is ready. Recording only starts if Didomi permits it for the
 * configured LogRocket vendor: an explicit grant under consent-based
 * regulations, or the default-enabled vendor under US opt-out regulations
 * (CPA/CCPA). If consent is withdrawn, LogRocket.uninstall() stops the session;
 * granting consent again reloads the page so the SDK can re-initialize cleanly.
 *
 * If Didomi is disabled for the current brand, LogRocket bypasses the consent
 * gate entirely and runs unconditionally (matches index.jsx's IS_DIDOMI_ENABLED
 * resolution: Garage and Dynamite each have their own enable flag).
 *
 * Config values (IS_LOGROCKET_ENABLED, LOGROCKET_APP_ID, LOGROCKET_DIDOMI_VENDOR_ID,
 * IS_DIDOMI_ENABLED_GARAGE, IS_DIDOMI_ENABLED_DYNAMITE) are stored as per-site objects
 * (e.g. {DynamiteGarageCA: true, DynamiteGarageUS: true}), so they must be resolved
 * via getSiteEnvVarValue(app, key, siteId) rather than read directly off `app`.
 *
 * LogRocket is enabled/app-ID/vendor-ID brand-split: the brand-suffixed key
 * (IS_LOGROCKET_ENABLED_GARAGE / _DYNAMITE, etc.) wins, falling back to the
 * un-suffixed key when the brand-specific pref is unset (getBrandEnvVarValue).
 */
export function LogRocketScript({userId, traits}) {
    const previousUserId = useRef(null)
    const {site} = useMultiSite()
    const {isGarage} = useBrand()
    const siteId = site?.id

    useEffect(() => {
        const {app} = getConfig()

        const isLogRocketEnabled =
            getBrandEnvVarValue(app, 'IS_LOGROCKET_ENABLED', siteId, isGarage) === 'true'
        const logRocketAppId = getBrandEnvVarValue(app, 'LOGROCKET_APP_ID', siteId, isGarage)

        if (!isLogRocketEnabled || !logRocketAppId) return undefined

        const start = (isConsentStillGranted) =>
            deferScriptLoading(() => {
                initLogRocket(logRocketAppId, app.ENVIRONMENT_TYPE).then((sdk) => {
                    if (!sdk) return

                    // Consent may have been withdrawn while the SDK was loading.
                    if (isConsentStillGranted && !isConsentStillGranted()) {
                        disableLogRocket()
                        return
                    }

                    // Identity changed mid-session (logout / account switch) — start a
                    // fresh recording so the previous shopper isn't cross-associated.
                    if (previousUserId.current && previousUserId.current !== userId) {
                        startNewLogRocketSession()
                    }
                    previousUserId.current = userId

                    if (userId) identifyLogRocket(userId, traits)
                })
            })

        // Resolve Didomi's enabled state per-brand, same as index.jsx's IS_DIDOMI_ENABLED.
        // Using the Garage flag unconditionally here would make LogRocket bypass consent
        // on Dynamite sites whenever Garage's flag happened to be off, even though Didomi
        // is actually running and gating other vendors there.
        const isDidomiEnabled = isGarage
            ? getSiteEnvVarValue(app, 'IS_DIDOMI_ENABLED_GARAGE', siteId) === 'true'
            : getSiteEnvVarValue(app, 'IS_DIDOMI_ENABLED_DYNAMITE', siteId) === 'true'

        if (!isDidomiEnabled) {
            start()
            return undefined
        }

        const vendorId = getBrandEnvVarValue(app, 'LOGROCKET_DIDOMI_VENDOR_ID', siteId, isGarage)

        // Without a configured vendor we cannot verify consent, so stay off.
        if (!vendorId) return undefined

        const isConsentStillGranted = () => hasLogRocketConsent(vendorId)

        // Start immediately if consent is already granted, otherwise wait for it.
        if (isConsentStillGranted()) start(isConsentStillGranted)

        const onConsentChanged = () => {
            if (isConsentStillGranted()) {
                // After uninstall the SDK cannot re-init on the same page load.
                if (wasUninstalled) {
                    window.location.reload()
                    return
                }
                start(isConsentStillGranted)
            } else {
                disableLogRocket()
            }
        }

        if (window.Didomi?.on) {
            window.Didomi.on('consent.changed', onConsentChanged)
            return () => window.Didomi?.off?.('consent.changed', onConsentChanged)
        }

        return undefined
    }, [userId, siteId, isGarage])

    return null
}

LogRocketScript.propTypes = {
    // SHA-256 hashed email for LogRocket.identify (registered shoppers only) — never raw PII.
    userId: PropTypes.string,
    // Optional non-PII traits to attach to the LogRocket user.
    traits: PropTypes.object
}